import { useMemo } from "react";
import { CascadeSIG, type CascadeBlock } from "../components/fiscal/CascadeSIG.js";
import { FiscalYearSelector } from "../components/fiscal/FiscalYearSelector.js";
import { FiscalCard, FiscalShell, LoadingSkeleton } from "../components/fiscal/Shell.js";
import { useFiscalSelection } from "../hooks/useFiscalSelection.js";
import { useT } from "../i18n/index.js";
import { estimateIS, IS_REDUCED_CAP } from "../lib/estimate-is.js";
import { formatMoney, toNumber } from "../lib/format-fr.js";
import { trpc } from "../lib/trpc.js";

export default function FiscalSig() {
  const { companyId, fiscalYearId, setCompanyId, setFiscalYearId } = useFiscalSelection();
  const t = useT();

  const calcQuery = trpc.fiscalCalculations.getByFiscalYear.useQuery(
    { companyId: companyId ?? 0, fiscalYearId: fiscalYearId ?? 0 },
    { enabled: companyId !== null && fiscalYearId !== null },
  );
  const achatsQuery = trpc.fiscalAggregates.achatsBreakdown.useQuery(
    { companyId: companyId ?? 0, fiscalYearId: fiscalYearId ?? 0 },
    { enabled: companyId !== null && fiscalYearId !== null, retry: false },
  );
  const achats = achatsQuery.data;

  const calc = calcQuery.data;

  const blocks = useMemo<CascadeBlock[]>(() => {
    if (!calc) return [];
    const ca = toNumber(calc.caHt);
    const pct = (v: string | number | null | undefined): number | null => {
      const n = toNumber(v);
      if (n === null || ca === null || ca <= 0) return null;
      return (n / ca) * 100;
    };
    return [
      {
        label: t("fiscalSig.blockCaLabel"),
        formula: t("fiscalSig.blockCaFormula"),
        cumulative: calc.caHt,
        annualized: calc.caHt,
        pctOfCaHt: 100,
        tone: "emphasis",
      },
      {
        label: t("fiscalSig.blockMargeLabel"),
        formula: t("fiscalSig.blockMargeFormula"),
        cumulative: calc.margeGlobaleCumulative,
        annualized: calc.margeGlobaleAnnualized,
        pctOfCaHt: pct(calc.margeGlobaleCumulative),
      },
      {
        label: t("fiscalSig.blockVaLabel"),
        formula: t("fiscalSig.blockVaFormula"),
        cumulative: calc.vaCumulative,
        annualized: calc.vaAnnualized,
        pctOfCaHt: pct(calc.vaCumulative),
      },
      {
        label: t("fiscalSig.blockEbeLabel"),
        formula: t("fiscalSig.blockEbeFormula"),
        cumulative: calc.ebeCumulative,
        annualized: calc.ebeAnnualized,
        pctOfCaHt: pct(calc.ebeCumulative),
      },
      {
        label: t("fiscalSig.blockRexLabel"),
        formula: t("fiscalSig.blockRexFormula"),
        cumulative: calc.resultatExploitationCumulative,
        annualized: calc.resultatExploitationAnnualized,
        pctOfCaHt: pct(calc.resultatExploitationCumulative),
      },
      {
        label: t("fiscalSig.blockRnLabel"),
        formula: t("fiscalSig.blockRnFormula"),
        cumulative: calc.resultatNetCumulative,
        annualized: calc.resultatNetAnnualized,
        pctOfCaHt: pct(calc.resultatNetCumulative),
        tone: "emphasis",
      },
      {
        label: t("fiscalSig.blockCafLabel"),
        formula: t("fiscalSig.blockCafFormula"),
        cumulative: calc.cafCumulative,
        annualized: calc.cafAnnualized,
        pctOfCaHt: pct(calc.cafCumulative),
        tone: "emphasis",
      },
    ];
  }, [calc, t]);

  const toolbar = (
    <FiscalYearSelector
      companyId={companyId}
      fiscalYearId={fiscalYearId}
      onChangeCompany={setCompanyId}
      onChangeFiscalYear={(fy) => setFiscalYearId(fy?.id ?? null)}
    />
  );

  return (
    <FiscalShell subtitle={t("fiscalSig.subtitle")} toolbar={toolbar}>
      {(companyId === null || fiscalYearId === null) && (
        <FiscalCard>
          <p className="text-sm text-neutral-400">{t("fiscalSig.selectCompanyFy")}</p>
        </FiscalCard>
      )}
      {fiscalYearId !== null && calcQuery.isLoading && (
        <FiscalCard>
          <LoadingSkeleton rows={6} />
        </FiscalCard>
      )}
      {fiscalYearId !== null && !calcQuery.isLoading && !calc && (
        <FiscalCard title={t("fiscalSig.noKpisTitle")}>
          <p className="text-sm text-neutral-400">
            {t("fiscalSig.noKpisHintPre")} <strong>{t("fiscalSig.recomputeKpisLabel")}</strong>{" "}
            {t("fiscalSig.noKpisHintSuf")}
          </p>
        </FiscalCard>
      )}
      {calc && (
        <FiscalCard title={t("fiscalSig.cascadeTitle")} subtitle={t("fiscalSig.cascadeSubtitle")}>
          <CascadeSIG blocks={blocks} />
        </FiscalCard>
      )}

      {/* F-recettes-encaissees Fase B — IS estimado 15%/25% sobre o résultat
          d'exploitation acumulado. Estimativa ANTES dos retraitements (2058-A). */}
      {calc &&
        (() => {
          const rex = toNumber(calc.resultatExploitationCumulative) ?? 0;
          const is = estimateIS(rex);
          return (
            <FiscalCard title={t("fiscalSig.isCardTitle")} subtitle={t("fiscalSig.isCardSubtitle")}>
              {rex <= 0 ? (
                <p className="text-sm text-neutral-400">{t("fiscalSig.isNoProfit")}</p>
              ) : (
                <div className="space-y-1.5 text-sm max-w-xl">
                  <div className="flex justify-between gap-4">
                    <span className="text-neutral-400">{t("fiscalSig.isRowBase")}</span>
                    <span className="tabular-nums font-semibold">{formatMoney(rex)}</span>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span className="text-neutral-400">
                      {t("fiscalSig.isRowTranche15Pre")} {formatMoney(is.base15, { decimals: 0 })} ×
                      15%
                    </span>
                    <span className="tabular-nums">−{formatMoney(is.is15)}</span>
                  </div>
                  {is.base25 > 0 && (
                    <div className="flex justify-between gap-4">
                      <span className="text-neutral-400">
                        {t("fiscalSig.isRowTranche25Pre")}{" "}
                        {formatMoney(IS_REDUCED_CAP, { decimals: 0 })} × 25%
                      </span>
                      <span className="tabular-nums">−{formatMoney(is.is25)}</span>
                    </div>
                  )}
                  <div className="flex justify-between gap-4 border-t border-neutral-800 pt-1.5">
                    <span className="text-neutral-300">{t("fiscalSig.isRowTotal")}</span>
                    <span className="tabular-nums text-red-300 font-semibold">
                      −{formatMoney(is.total)}
                    </span>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span className="text-neutral-200 font-semibold">
                      {t("fiscalSig.isRowNet")}
                    </span>
                    <span className="tabular-nums text-lime font-bold">
                      {formatMoney(is.netAfterIs)}
                    </span>
                  </div>
                </div>
              )}
            </FiscalCard>
          );
        })()}

      {achats && (
        <FiscalCard
          title={t("fiscalSig.achatsTitle")}
          subtitle={t("fiscalSig.achatsSubtitle")}
          padded={false}
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-neutral-500 border-b border-neutral-800">
                <tr>
                  <th className="px-4 py-2">{t("fiscalSig.colOrigin")}</th>
                  <th className="px-4 py-2">{t("fiscalSig.colNumber")}</th>
                  <th className="px-4 py-2">{t("fiscalSig.colStatus")}</th>
                  <th className="px-4 py-2">{t("fiscalSig.colDate")}</th>
                  <th className="px-4 py-2 text-right">{t("fiscalSig.colAmount")}</th>
                </tr>
              </thead>
              <tbody>
                {achats.intercompany.items.length === 0 && achats.technician.items.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-5 text-center text-neutral-500">
                      {t("fiscalSig.emptyState")}
                    </td>
                  </tr>
                ) : (
                  <>
                    {achats.intercompany.items.map((it, i) => (
                      <tr key={`ic-${i}`} className="border-b border-neutral-900">
                        <td className="px-4 py-1.5 text-sky-300">
                          {t("fiscalSig.intercompanyLabel")}
                        </td>
                        <td className="px-4 py-1.5">{it.invoiceNumber}</td>
                        <td className="px-4 py-1.5 text-neutral-400">{it.status}</td>
                        <td className="px-4 py-1.5 tabular-nums text-neutral-400">
                          {it.date ?? "—"}
                        </td>
                        <td className="px-4 py-1.5 text-right tabular-nums">
                          {formatMoney(it.amount)}
                        </td>
                      </tr>
                    ))}
                    {achats.technician.items.map((it, i) => (
                      <tr key={`tech-${i}`} className="border-b border-neutral-900">
                        <td className="px-4 py-1.5 text-amber-300">
                          {t("fiscalSig.technicianLabel")}
                        </td>
                        <td className="px-4 py-1.5">{it.invoiceNumber}</td>
                        <td className="px-4 py-1.5 text-neutral-400">{it.status}</td>
                        <td className="px-4 py-1.5 tabular-nums text-neutral-400">
                          {it.date ?? "—"}
                        </td>
                        <td className="px-4 py-1.5 text-right tabular-nums">
                          {formatMoney(it.amount)}
                        </td>
                      </tr>
                    ))}
                  </>
                )}
              </tbody>
              <tfoot>
                <tr className="border-t border-neutral-800 text-neutral-400">
                  <td className="px-4 py-1.5" colSpan={4}>
                    Intercompany ({achats.intercompany.count})
                  </td>
                  <td className="px-4 py-1.5 text-right tabular-nums">
                    {formatMoney(achats.intercompany.total)}
                  </td>
                </tr>
                <tr className="text-neutral-400">
                  <td className="px-4 py-1.5" colSpan={4}>
                    Técnico ({achats.technician.count})
                  </td>
                  <td className="px-4 py-1.5 text-right tabular-nums">
                    {formatMoney(achats.technician.total)}
                  </td>
                </tr>
                <tr className="border-t-2 border-neutral-700 font-semibold">
                  <td className="px-4 py-2" colSpan={4}>
                    Achats consommés (total)
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-red-300">
                    {formatMoney(achats.grandTotal)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </FiscalCard>
      )}
    </FiscalShell>
  );
}
